<template>
  <div
    ref="block"
    class="block"
    :style="styledBlock"
    :class="[
      variant,
      theme,
      'stroke-' + stroke,
      { expandable: expandable !== 'none' },
      { expanded },
      { ready: isExpandableReady || true },
    ]"
  >
    <header
      v-if="!noHeader"
      ref="header"
      class="block-header"
      @click="expandable !== 'none' ? clickExpand($event) : null"
    >
      <div class="header-content"><slot name="header" /></div>
      <Icon
        v-if="expandable !== 'none'"
        class="icon-expand"
        :name="expanded ? 'hideContent' : 'unrollContent'"
        color="neutral-50"
        size="2.8rem"
      />
    </header>
    <div ref="content" :class="{ expanded }" class="content">
      <slot />
    </div>
  </div>
</template>
<script lang="ts" setup>
interface BlockProps {
  theme?: string;
  stroke?: string;
  variant?: 'tonal' | 'outlined';
  expandable?: 'none' | 'closed' | 'opened';
  noHeader?: boolean;
}
const props = defineProps({
  theme: { default: 'neutral-50' },
  stroke: { default: 'm' },
  variant: { default: 'outlined' },
  expandable: { default: 'opened' },
  noHeader: { type: Boolean, default: false }
});
const header = ref(null);
const block = ref(null);
const content = ref(null);
const {
  style: styledBlock,
  isReady: isExpandableReady,
  handler: clickExpand,
  expanded,
} = useBlockExpandable(block, header, content, props.expandable);
</script>
<style lang="scss" scoped>
header {
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  padding: 2rem;
  transition: border-color 300ms $ease-out-quad;
  border-bottom: 1px solid rgb(0 0 0 / 0%);
  gap: var(--gap_n);
}
.content {
  display: flex;
  visibility: hidden;
  flex-direction: column;
  padding: var(--gap_m);
  transition: opacity 300ms $ease-out-quad;
  opacity: 0;
  &.expanded {
    visibility: visible;
  }
}
.block {
  display: flex;
  position: relative;
  flex-direction: column;
  overflow: hidden;
  @include font('s', 500);
  &.expandable {
    header {
      grid-template-columns: 1fr 3rem;
      cursor: pointer;
    }
  }
  &.expanded {
    header {
      --border_thickness: var(--stroke_thickness_n);

      border-bottom-width: var(--border_thickness);
      border-bottom-style: solid;
      border-color: var(--color_secondary_lighter);
    }
    .content {
      opacity: 1;
    }
  }
}
.icon-expand {
  color: var(--color_secondary);
}
</style>
